/**
 * Custom hook for MathLive integration
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import type { MathfieldElement } from 'mathlive';
import type { ValidationResult, MathLiveConfig, VirtualKeyboardConfig } from '../types';
import { getMathLiveOptions, DEFAULT_KEYBOARD_CONFIG } from '../utils/mathLiveConfig';
import { normalizeLatex } from '../utils/latexHelpers';
import { validateLatex as validateLatexApi } from '../../../../services/latexToSpine.service';

interface UseMathLiveProps {
  value: string;
  onChange: (value: string) => void;
  onValidationChange?: (validation: ValidationResult) => void;
  config?: Partial<MathLiveConfig>;
  keyboardConfig?: VirtualKeyboardConfig;
  disabled?: boolean;
}

interface UseMathLiveReturn {
  mathfieldRef: React.RefObject<MathfieldElement>;
  isLoaded: boolean;
  error: string | null;
  validation: ValidationResult | null;
  isValidating: boolean;
  focus: () => void;
  blur: () => void;
  insert: (latex: string) => void;
  setValue: (latex: string) => void;
  getValue: () => string;
  validateNow: () => Promise<void>;
}

export const useMathLive = ({
  value,
  onChange,
  onValidationChange,
  config = {},
  keyboardConfig = DEFAULT_KEYBOARD_CONFIG,
  disabled = false,
}: UseMathLiveProps): UseMathLiveReturn => {
  const mathfieldRef = useRef<MathfieldElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const hasInitialValidation = useRef(false);

  // Use ref to store the callback to avoid dependency issues
  const onValidationChangeRef = useRef(onValidationChange);
  useEffect(() => {
    onValidationChangeRef.current = onValidationChange;
  }, [onValidationChange]);

  // Store current value in ref for validation function
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  // Initialize MathLive
  useEffect(() => {
    let mounted = true;

    const initializeMathLive = async () => {
      try {
        // Dynamically import MathLive to support code splitting
        const { MathfieldElement } = await import('mathlive');

        if (!mounted) return;

        // Configure MathLive font directory for Vite before defining the element
        MathfieldElement.fontsDirectory = '/fonts/';

        // Ensure custom element is defined
        if (!customElements.get('math-field')) {
          customElements.define('math-field', MathfieldElement);
        }

        setIsLoaded(true);
        setError(null);
      } catch (err) {
        console.error('Failed to load MathLive:', err);
        setError('Failed to load math editor');
        setIsLoaded(false);
      }
    };

    initializeMathLive();

    return () => {
      mounted = false;
    };
  }, []);

  // Configure mathfield when loaded
  useEffect(() => {
    const mathfield = mathfieldRef.current;
    if (!mathfield || !isLoaded) return;

    // Apply mathfield options
    const options = getMathLiveOptions(config);
    Object.assign(mathfield, options);

    // Configure global virtual keyboard (MathLive 0.90+ uses a global singleton)
    if (window.mathVirtualKeyboard) {
      window.mathVirtualKeyboard.layouts = keyboardConfig.layouts;
    }

    // Set disabled state
    mathfield.readOnly = disabled;
  }, [isLoaded, config, keyboardConfig, disabled]);

  // Sync value from parent
  useEffect(() => {
    const mathfield = mathfieldRef.current;
    if (!mathfield || !isLoaded) return;

    if (mathfield.getValue() !== value) {
      mathfield.setValue(value);
    }
  }, [value, isLoaded]);

  // Validate expression via backend API
  const validateExpression = useCallback(async (latex: string): Promise<void> => {
    if (!latex || latex.trim().length === 0) {
      const emptyValidation: ValidationResult = {
        isValid: false,
        errors: ['Expression cannot be empty'],
        warnings: [],
      };
      setValidation(emptyValidation);
      onValidationChangeRef.current?.(emptyValidation);
      return;
    }

    setIsValidating(true);
    try {
      const result = await validateLatexApi(latex);
      const newValidation: ValidationResult = {
        isValid: result.isValid,
        errors: result.errors,
        warnings: [], // Backend doesn't return warnings currently
      };
      setValidation(newValidation);
      onValidationChangeRef.current?.(newValidation);
    } catch (err: any) {
      // If API fails, set a validation error
      const errorValidation: ValidationResult = {
        isValid: false,
        errors: [err.message || 'Failed to validate expression'],
        warnings: [],
      };
      setValidation(errorValidation);
      onValidationChangeRef.current?.(errorValidation);
    } finally {
      setIsValidating(false);
    }
  }, []);

  // Expose validateNow for external triggering (e.g., onBlur)
  const validateNow = useCallback(async () => {
    await validateExpression(valueRef.current);
  }, [validateExpression]);

  // Initial validation on mount (when value exists and component is loaded)
  useEffect(() => {
    if (isLoaded && value && !hasInitialValidation.current) {
      hasInitialValidation.current = true;
      validateExpression(value);
    }
  }, [isLoaded, value, validateExpression]);

  // Debounced validation on keypress (500ms delay)
  useEffect(() => {
    // Skip if not loaded or no value
    if (!isLoaded || !value.trim()) return;

    // Skip initial validation (handled above)
    if (!hasInitialValidation.current) return;

    const timer = setTimeout(() => {
      validateExpression(value);
    }, 500);

    return () => clearTimeout(timer);
  }, [value, isLoaded, validateExpression]);

  // Set up event listeners
  useEffect(() => {
    const mathfield = mathfieldRef.current;
    if (!mathfield || !isLoaded) return;

    const handleInput = () => {
      const rawValue = mathfield.getValue();
      const newValue = normalizeLatex(rawValue);
      if (newValue !== value) {
        onChange(newValue);
      }
    };

    const handleFocus = () => {
      mathfield.classList.add('focused');
    };

    const handleBlur = () => {
      mathfield.classList.remove('focused');
    };

    mathfield.addEventListener('input', handleInput);
    mathfield.addEventListener('focus', handleFocus);
    mathfield.addEventListener('blur', handleBlur);

    return () => {
      mathfield.removeEventListener('input', handleInput);
      mathfield.removeEventListener('focus', handleFocus);
      mathfield.removeEventListener('blur', handleBlur);
    };
  }, [isLoaded, value, onChange]);

  // Helper functions
  const focus = useCallback(() => {
    if (mathfieldRef.current && isLoaded) {
      mathfieldRef.current.focus();
    }
  }, [isLoaded]);

  const blur = useCallback(() => {
    if (mathfieldRef.current && isLoaded) {
      mathfieldRef.current.blur();
    }
  }, [isLoaded]);

  const insert = useCallback((latex: string) => {
    if (mathfieldRef.current && isLoaded) {
      mathfieldRef.current.insert(latex);
      // The input event will trigger onChange automatically
    }
  }, [isLoaded]);

  const setValue = useCallback((latex: string) => {
    if (mathfieldRef.current && isLoaded) {
      mathfieldRef.current.setValue(latex);
      // Manually trigger onChange since setValue doesn't fire input event
      onChange(latex);
    }
  }, [isLoaded, onChange]);

  const getValue = useCallback((): string => {
    if (mathfieldRef.current && isLoaded) {
      return mathfieldRef.current.getValue();
    }
    return value;
  }, [isLoaded, value]);

  return {
    mathfieldRef,
    isLoaded,
    error,
    validation,
    isValidating,
    focus,
    blur,
    insert,
    setValue,
    getValue,
    validateNow,
  };
};